"use client"

import JSZip from "jszip"
import {
  Mail,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  Loader2,
  Users,
  ArrowRight,
  ArrowLeft,
} from "lucide-react"
import type React from "react"
import { useState, useRef } from "react"
import { Helmet } from "react-helmet-async"
import { useNavigate } from "react-router-dom"

import { ROUTES } from "../../constants/routes"
import { DirectInviteModal } from "../../components/students/DirectInviteModal"
import { Alert, AlertDescription } from "../../components/ui/Alert"
import { Button } from "../../components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card"
import { supabase } from "../../lib/supabase"
import { importStudentData, getUserCompanyId } from "../../services/studentImportService"
import { processCSV, type ProcessedRow, type InvalidEmailRecord } from "../../utils/csvValidation"
import { useBackgroundJob } from "../../hooks/useBackgroundJob"


interface UploadResult {
  success: boolean
  message: string
  processed?: number
  errors?: string[]
  warnings?: string[]
  invalidEmails?: InvalidEmailRecord[]
  validData?: ProcessedRow[]
}

export default function UploadStudentsPage() {
  const navigate = useNavigate();
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDownloadingKit, setIsDownloadingKit] = useState(false)
  const [showDirectInviteModal, setShowDirectInviteModal] = useState(false)
  const [backgroundJobId, setBackgroundJobId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Live progress tracking for queued background imports
  const backgroundJob = useBackgroundJob(backgroundJobId)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleFileSelect = (file: File) => {
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      setUploadResult({
        success: false,
        message: "Please select a valid CSV file.",
      })
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      // 10MB limit (increased from 5MB to support large gyms/franchises)
      setUploadResult({
        success: false,
        message: "File size must be less than 10MB.",
      })
      return
    }

    setSelectedFile(file)
    setUploadResult(null)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return;
  
    setIsUploading(true);
    setUploadResult(null);
  
    try {
      // Read the CSV file content
      const csvText = await selectedFile.text();
      
      // Process and validate the CSV
      const result = processCSV(csvText);
      
      // Auto-download bad records if any exist
      if (result.invalidEmails.length > 0) {
        downloadBadRecordsCSV(result.invalidEmails);
      }
      
      // Auto-submit valid records if we have any
      if (result.validRowCount > 0) {
        // Get the user's company ID
        const companyId = await getUserCompanyId();
        
        if (!companyId) {
          setUploadResult({
            success: false,
            message: "Unable to determine your company. Please make sure you're properly authenticated.",
            errors: ["No company relationship found for current user"],
            invalidEmails: result.invalidEmails
          });
          setIsUploading(false);
          return;
        }
        
        // Auto-submit valid records
        setIsSubmitting(true);
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log('[UploadStudentData] Submitting', result.validRowCount, 'valid rows to backend');
        }
        const submitResult = await importStudentData({
          students: result.validRows,
          company_id: companyId,
        });
        
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log('[UploadStudentData] Backend response:', {
            success: submitResult.success,
            imported_count: submitResult.imported_count,
            message: submitResult.message,
            errors: submitResult.errors?.length || 0,
            validation_errors: submitResult.validation_errors?.length || 0,
          });
        }
        
        setIsSubmitting(false);
        
        // Handle queued background import (large CSV)
        if (submitResult.queued && submitResult.job_id) {
          setBackgroundJobId(submitResult.job_id);
          setUploadResult({
            success: true,
            message: submitResult.message || `Your import of ${result.validRowCount} records has been queued. You can navigate away — we'll send you a notification when it's done.`,
            processed: 0,
            errors: submitResult.validation_errors || [],
            warnings: submitResult.warnings || [],
            invalidEmails: result.invalidEmails,
            validData: result.validRows,
          });
        } else if (submitResult.success) {
          const duplicateCount = result.invalidEmails.filter(e => e.isDuplicate).length;
          const invalidCount = result.invalidEmails.length - duplicateCount;
          const message = result.invalidEmails.length > 0
            ? `Successfully imported ${submitResult.imported_count} student records! ${duplicateCount > 0 ? `${duplicateCount} duplicate email(s) and ` : ''}${invalidCount > 0 ? `${invalidCount} record(s) with missing/invalid emails ` : ''}were skipped and downloaded for review.`
            : `Successfully imported ${submitResult.imported_count} student records! They are now in your system and ready for processing.`;

          setUploadResult({
            success: true,
            message,
            processed: submitResult.imported_count,
            errors: submitResult.validation_errors || [],
            warnings: submitResult.warnings || [],
            invalidEmails: result.invalidEmails,
            validData: result.validRows
          });
        } else {
          // Log detailed errors for debugging but don't show to user
          if (submitResult.errors && submitResult.errors.length > 0) {
            console.error('Import errors (logged for debugging):', submitResult.errors);
          }
          
          setUploadResult({
            success: false,
            message: submitResult.message || "Failed to import student data. Please try again or contact support if the issue persists.",
            // Only show validation errors (user input issues), not technical errors
            errors: submitResult.validation_errors || [],
            warnings: submitResult.warnings,
            invalidEmails: result.invalidEmails
          });
        }
        
        // Clear file selection after processing
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        const totalIssues = result.invalidEmails.length + result.errors.length;
        const duplicateCount = result.invalidEmails.filter(e => e.isDuplicate).length;
        const emailIssueCount = result.invalidEmails.length - duplicateCount;
        
        let message = `No valid records found. `;
        if (result.invalidEmails.length > 0) {
          const parts = [];
          if (duplicateCount > 0) parts.push(`${duplicateCount} duplicate email(s)`);
          if (emailIssueCount > 0) parts.push(`${emailIssueCount} record(s) with missing/invalid emails`);
          message += `${parts.join(' and ')} (downloaded for review). `;
        }
        if (result.errors.length > 0) {
          message += `Additionally, ${result.errors.length} record(s) failed other validations (see errors below).`;
        }
        if (totalIssues === 0) {
          message = `No valid records found. Please check your CSV file format.`;
        }
        
        setUploadResult({
          success: false,
          message: message.trim(),
          processed: result.validRowCount,
          errors: result.errors,
          warnings: result.warnings,
          invalidEmails: result.invalidEmails
        });
      }
      
    } catch (error) {
      console.error('CSV processing error:', error);
      setUploadResult({
        success: false,
        message: "Failed to process CSV file. Please check the file format.",
        errors: [error instanceof Error ? error.message : "Unknown error occurred"],
      });
    } finally {
      setIsUploading(false);
      setIsSubmitting(false);
    }
  };

  const downloadRosterUploadKit = async () => {
    setIsDownloadingKit(true)
    try {
      // Get current session - verify_jwt = true means Supabase validates the token
      // before the function code runs, so we need a valid token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      if (sessionError || !session) {
        window.alert('You must be logged in to download the upload kit')
        return
      }

      // Refresh session to ensure token is fresh and update the Supabase client's session
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
      
      if (refreshError || !refreshData.session?.access_token) {
        console.error('Failed to refresh session:', refreshError)
        window.alert('Session expired. Please log in again.')
        return
      }

      // Use the refreshed token - manually pass Authorization header like import-students does
      const accessToken = refreshData.session.access_token

      // Call Edge Function with manual Authorization header (same pattern as import-students)
      const { data, error } = await supabase.functions.invoke('generate-roster-upload-kit', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      })

      if (error) {
        console.error('Error generating upload kit:', error)
        console.error('Error details:', {
          message: error.message,
          context: (error as any).context,
          data,
        })
        
        // Try to extract actual error message from response
        let errorMessage = error.message || 'Failed to generate upload kit'
        
        // Check if error details are in context
        const errorContext = (error as any).context
        if (errorContext) {
          // Try to parse error from context body
          if (errorContext.body && typeof errorContext.body === 'object' && 'error' in errorContext.body) {
            errorMessage = errorContext.body.error
          } else if (errorContext.message) {
            errorMessage = errorContext.message
          }
        }
        
        // Also check data field (sometimes Supabase puts error there)
        if (data && typeof data === 'object' && 'error' in data) {
          errorMessage = (data as any).error || errorMessage
        }
        
        // Provide more specific error messages
        if (errorMessage.includes('401') || errorMessage.includes('Unauthorized') || errorMessage.includes('Missing authorization')) {
          window.alert('Authentication failed. Please log out and log back in, then try again.')
        } else if (errorMessage.includes('No company found')) {
          window.alert('No company found for your account. Please contact support.')
        } else {
          window.alert(`Failed to generate upload kit: ${errorMessage}`)
        }
        return
      }

      if (!data.success || !data.files) {
        window.alert('Failed to generate upload kit. Please try again.')
        return
      }

      // Create ZIP file using JSZip
      const zip = new JSZip()

      // Add CSV template
      const templateCSV = window.atob(data.files['Student Import Template.csv'])
      zip.file('Student Import Template.csv', templateCSV)

      // Add subscriptions CSV
      const subscriptionsCSV = window.atob(data.files['subscriptions.csv'])
      zip.file('subscriptions.csv', subscriptionsCSV)

      // Add discounts CSV
      const discountsCSV = window.atob(data.files['discounts.csv'])
      zip.file('discounts.csv', discountsCSV)

      // Add locations CSV
      if (data.files['locations.csv']) {
        const locationsCSV = window.atob(data.files['locations.csv'])
        zip.file('locations.csv', locationsCSV)
      }

      // Add README (HTML for now, can be converted to PDF later)
      const readmeHTML = window.atob(data.files['README.html'])
      zip.file('README.html', readmeHTML)

      // Generate ZIP blob
      const zipBlob = await zip.generateAsync({ type: 'blob' })

      // Download ZIP file
      const url = window.URL.createObjectURL(zipBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'RollOps Roster Upload Kit.zip'
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }, 100)
    } catch (error) {
      console.error('Error downloading upload kit:', error)
      window.alert('Failed to download upload kit. Please try again.')
    } finally {
      setIsDownloadingKit(false)
    }
  }

  const downloadBadRecordsCSV = (invalidRecords: InvalidEmailRecord[]) => {
    if (!invalidRecords || invalidRecords.length === 0) return

    // Create CSV header
    const headers = 'first_name,last_name,email,phone_number,family_id,original_start_date,contract_start_date,contract_end_date,subscription_price,subscription_name,subscription_id,primary_location_id,punch_card_total,punch_card_limit,is_trial_member,notes,error_reason\n'
    
    // Create rows for invalid records
    const rows = invalidRecords.map(record => {
      const firstName = record.first_name || ''
      const lastName = record.last_name || ''
      const email = record.email || ''
      const reason = record.reason || 'Invalid email'
      // Escape commas and quotes in CSV
      const escapeCSV = (str: string) => {
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`
        }
        return str
      }
      return `${escapeCSV(firstName)},${escapeCSV(lastName)},${escapeCSV(email)},,,,,,,,,,,,${escapeCSV(reason)}`
    }).join('\n')

    const csvContent = headers + rows + '\n'
    
    // Add BOM for Excel compatibility
    const BOM = '\uFEFF'
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Failed_Student_Records_${new Date().toISOString().split('T')[0]}.csv`
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    setTimeout(() => {
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    }, 100)
  }

  return (
    <>
      <Helmet>
          <title>Upload Student Data</title>
          <meta name="robots" content="noindex,nofollow,noarchive" data-react-helmet="true"/>
          <meta name="googlebot" content="noindex,nofollow,noarchive" data-react-helmet="true"/>
      </Helmet>
      <div className="flex-1 flex flex-col min-h-0">
        {/* Page Header - matches Billing Settings / Damage Waivers */}
        <div className="border-b border-white/10 bg-[#111010] pb-3 md:pb-4 shrink-0">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">Upload Student Data</h1>
              <p className="text-white/80 mt-1">Import your existing student roster quickly and easily with our CSV upload tool.</p>
              <button
                onClick={() => setShowDirectInviteModal(true)}
                className="mt-3 text-sm text-[#9B1421] hover:text-[#7A0F1A] underline transition-colors"
              >
                Didn&apos;t mean to bulk upload? Invite a single student instead
              </button>
            </div>
            <button
              type="button"
              onClick={() => navigate(ROUTES.STUDENTS)}
              className="flex items-center gap-2 text-sm text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Students
            </button>
          </div>
        </div>

        {/* Content - fills remaining area */}
        <div className="flex-1 bg-[#111010] overflow-auto scrollbar-styled min-h-0 py-6 w-full">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Upload Section */}
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#EBFFEE] flex items-center">
                  <Upload className="w-5 h-5 mr-2 text-rollops-accent" />
                  Upload CSV File
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* File Upload Area */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragOver ? "border-[#9B1421] bg-[#9B1421]/10" : "border-[#504A4A] hover:border-[#9B1421]"
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <FileText className="w-12 h-12 text-[#A9A3A3] mx-auto mb-4" />
                  <p className="text-[#A9A3A3] mb-4">Drag and drop your CSV file here, or click to browse</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent"
                  >
                    Choose File
                  </Button>
                </div>

                {/* Selected File Info */}
                {selectedFile && (
                  <div className="bg-[#363232] p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[#EBFFEE]">{selectedFile.name}</p>
                        <p className="text-sm text-[#A9A3A3]">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <Button
                        onClick={() => {
                          setSelectedFile(null)
                          if (fileInputRef.current) fileInputRef.current.value = ""
                        }}
                        variant="outline"
                        size="sm"
                        className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#504A4A] bg-transparent"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                )}

                {/* Upload Button - Now auto-submits valid records */}
                <Button
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading || isSubmitting}
                  className="w-full bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-semibold py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading || isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {isSubmitting ? 'Importing Records...' : 'Processing CSV...'}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload & Import Student Data
                    </>
                  )}
                </Button>

                {/* Background Job Progress Card */}
                {backgroundJobId && backgroundJob.status && backgroundJob.status !== 'completed' && backgroundJob.status !== 'failed' && (
                  <Card className="border-blue-500 bg-blue-500/10">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3 mb-3">
                        <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />
                        <p className="font-medium text-blue-100">
                          {backgroundJob.status === 'pending' ? 'Import queued...' : 'Processing your import...'}
                        </p>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-3 mb-2">
                        <div
                          className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                          style={{
                            width: backgroundJob.totalCount > 0
                              ? `${Math.round((backgroundJob.processedCount / backgroundJob.totalCount) * 100)}%`
                              : '0%'
                          }}
                        />
                      </div>
                      <p className="text-sm text-gray-400">
                        {backgroundJob.processedCount} of {backgroundJob.totalCount} records processed
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        You can navigate away — we will send you a notification when done.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Background Job Completed */}
                {backgroundJobId && backgroundJob.status === 'completed' && (
                  <Alert className="border-green-500 bg-green-500/10">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <AlertDescription className="text-green-400">
                      <div>
                        <p className="font-medium text-[#EBFFEE]">
                          Import complete! {backgroundJob.result?.imported_count ?? backgroundJob.totalCount} records imported.
                        </p>
                        {backgroundJob.result?.skipped_count > 0 && (
                          <p className="text-sm mt-1 text-yellow-400">
                            {backgroundJob.result.skipped_count} duplicate record(s) were skipped.
                          </p>
                        )}
                        <div className="mt-4">
                          <Button
                            onClick={() => navigate('/students?tab=draft')}
                            className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center gap-2"
                          >
                            <Users className="w-4 h-4" />
                            See your draft students
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Background Job Failed */}
                {backgroundJobId && backgroundJob.status === 'failed' && (
                  <Alert className="border-red-500 bg-red-500/10">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <AlertDescription className="text-red-400">
                      <p className="font-medium">Import failed</p>
                      <p className="text-sm mt-1">{backgroundJob.error || 'An unexpected error occurred. Please try again or contact support.'}</p>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Validation feedback for queued imports (warnings, invalid emails skipped before queuing) */}
                {backgroundJobId && uploadResult && (uploadResult.invalidEmails?.length || 0) + (uploadResult.warnings?.length || 0) > 0 && (
                  <Alert className="border-yellow-500 bg-yellow-500/10">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    <AlertDescription className="text-yellow-400">
                      <div>
                        {uploadResult.warnings && uploadResult.warnings.length > 0 && (
                          <div className="mb-2">
                            <p className="text-sm font-medium">Warnings:</p>
                            <ul className="text-sm list-disc list-inside">
                              {uploadResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                            </ul>
                          </div>
                        )}
                        {uploadResult.invalidEmails && uploadResult.invalidEmails.length > 0 && (
                          <p className="text-sm">
                            {uploadResult.invalidEmails.length} record(s) with email issues were skipped before queuing.
                          </p>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Upload Result (synchronous imports / validation feedback) */}
                {uploadResult && !backgroundJobId && (
                  <Alert
                    className={`${
                      uploadResult.success ? "border-green-500 bg-green-500/10" : "border-red-500 bg-red-500/10"
                    }`}
                  >
                    {uploadResult.success ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <AlertDescription className={uploadResult.success ? "text-green-400" : "text-red-400"}>
                      <div>
                        <p className="font-medium text-[#EBFFEE]">{uploadResult.message}</p>
                        {uploadResult.processed !== undefined && (
                          <p className="text-sm mt-1">
                            {uploadResult.success
                              ? `${uploadResult.processed} records imported successfully`
                              : uploadResult.processed > 0
                                ? `${uploadResult.processed} valid record(s) found, but ${(uploadResult.invalidEmails?.length || 0) + (uploadResult.errors?.length || 0)} issue(s) prevent submission`
                                : `0 valid records found - all ${(uploadResult.invalidEmails?.length || 0) + (uploadResult.errors?.length || 0)} record(s) have validation issues`
                            }
                          </p>
                        )}

                        {/* See Draft Students Button - Only show on success */}
                        {uploadResult.success && (
                          <div className="mt-4">
                            <Button
                              onClick={() => navigate('/students?tab=draft')}
                              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center gap-2"
                            >
                              <Users className="w-4 h-4" />
                              See your draft students
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        
                        {/* Data Preview for successful validation */}
                        {uploadResult.success && uploadResult.validData && uploadResult.validData.length > 0 && (
                          <div className="mt-3 p-3 bg-green-900/20 rounded">
                            <p className="text-sm font-medium text-green-300 mb-2">Preview (first 3 records):</p>
                            <div className="text-xs space-y-1 max-h-32 overflow-y-auto">
                              {uploadResult.validData.slice(0, 3).map((row, index) => (
                                <div key={index} className="text-green-200">
                                  {row.first_name} {row.last_name} - {row.email}
                                  {row.phone_number && ` - ${row.phone_number}`}
                                  {row.subscription_name && ` - ${row.subscription_name}`}
                                </div>
                              ))}
                              {uploadResult.validData.length > 3 && (
                                <div className="text-green-300 italic">
                                  ... and {uploadResult.validData.length - 3} more records
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {uploadResult.warnings && uploadResult.warnings.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm font-medium text-yellow-400">Warnings:</p>
                            <ul className="text-sm text-yellow-300 list-disc list-inside max-h-32 overflow-y-auto">
                              {uploadResult.warnings.map((warning, index) => (
                                <li key={index}>{warning}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {uploadResult.invalidEmails && uploadResult.invalidEmails.length > 0 && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-medium text-orange-400">
                                Records with Email Issues (Skipped): {uploadResult.invalidEmails.length} record(s)
                              </p>
                              <Button
                                onClick={() => downloadBadRecordsCSV(uploadResult.invalidEmails || [])}
                                variant="outline"
                                size="sm"
                                className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent"
                              >
                                <Download className="w-3 h-3 mr-1" />
                                Download CSV
                              </Button>
                            </div>
                            <div className="text-xs mt-1 max-h-32 overflow-y-auto bg-orange-900/20 p-2 rounded">
                              <table className="w-full text-left">
                                <thead>
                                  <tr className="border-b border-orange-800">
                                    <th className="text-orange-300 pb-1">Row</th>
                                    <th className="text-orange-300 pb-1">Name</th>
                                    <th className="text-orange-300 pb-1">Email</th>
                                    <th className="text-orange-300 pb-1">Reason</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {uploadResult.invalidEmails.map((record, index) => (
                                    <tr key={index} className="border-b border-orange-900/50">
                                      <td className="text-orange-200 py-1">{record.row}</td>
                                      <td className="text-orange-200 py-1">
                                        {record.first_name || ''} {record.last_name || ''}
                                      </td>
                                      <td className="text-orange-200 py-1">{record.email || '(blank)'}</td>
                                      <td className="text-orange-200 py-1">{record.reason}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        {uploadResult.errors && uploadResult.errors.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm font-medium text-[#EBFFEE]">Errors:</p>
                            <ul className="text-sm text-[#A9A3A3] list-disc list-inside max-h-32 overflow-y-auto">
                              {uploadResult.errors.map((error, index) => (
                                <li key={index}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Instructions Section */}
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#EBFFEE] flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-rollops-accent" />
                  CSV Format Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Download Template */}
                <div className="bg-[#363232] p-4 rounded-lg">
                  <h3 className="font-semibold text-[#EBFFEE] mb-2">Get Started Quickly</h3>
                  <p className="text-[#A9A3A3] text-sm mb-3">
                    Download our template to ensure your CSV is formatted correctly.
                  </p>
                  <Button
                    onClick={downloadRosterUploadKit}
                    variant="red"
                    disabled={isDownloadingKit}
                  >
                    {isDownloadingKit ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating Kit...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download RollOps Roster Upload Kit
                      </>
                    )}
                  </Button>
                </div>

                {/* Required Columns */}
                <div className="bg-[#2D2727] border border-[#9B1421] p-4 rounded-lg border-l-4">
                  <h3 className="font-semibold text-[#EBFFEE] mb-3 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-rollops-accent" />
                    Required Columns
                  </h3>
                  <p className="text-sm text-[#A9A3A3] mb-3">
                    Your CSV needs only <strong className="text-[#EBFFEE]">3 columns</strong> to work. The header row must use these exact column names:
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <code className="text-[#EBFFEE] font-mono bg-[#111010] px-2 py-0.5 rounded text-xs">first_name</code>
                      <span className="text-text-primary font-semibold bg-[#9B1421]/20 border border-[#9B1421]/40 px-2 py-1 rounded">Required</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <code className="text-[#EBFFEE] font-mono bg-[#111010] px-2 py-0.5 rounded text-xs">last_name</code>
                      <span className="text-text-primary font-semibold bg-[#9B1421]/20 border border-[#9B1421]/40 px-2 py-1 rounded">Required</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <code className="text-[#EBFFEE] font-mono bg-[#111010] px-2 py-0.5 rounded text-xs">email</code>
                      <span className="text-text-primary font-semibold bg-[#9B1421]/20 border border-[#9B1421]/40 px-2 py-1 rounded">Required</span>
                    </div>
                  </div>
                  <p className="text-xs text-[#A9A3A3] mt-3 italic">
                    Common variations like &quot;First Name&quot; or &quot;Email Address&quot; are automatically recognized. All other columns are optional.
                  </p>
                </div>

                {/* Minimum Viable Example */}
                <div className="bg-[#111010] border border-[#504A4A] rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-[#363232] border-b border-[#504A4A]">
                    <h3 className="font-semibold text-[#EBFFEE] text-sm">Minimum CSV Example</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#504A4A]">
                          <th className="text-left px-4 py-2 text-[#9B1421] font-mono font-semibold text-xs">first_name</th>
                          <th className="text-left px-4 py-2 text-[#9B1421] font-mono font-semibold text-xs">last_name</th>
                          <th className="text-left px-4 py-2 text-[#9B1421] font-mono font-semibold text-xs">email</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-[#504A4A]/50">
                          <td className="px-4 py-1.5 text-[#EBFFEE] text-xs">Jane</td>
                          <td className="px-4 py-1.5 text-[#EBFFEE] text-xs">Smith</td>
                          <td className="px-4 py-1.5 text-[#EBFFEE] text-xs">jane@example.com</td>
                        </tr>
                        <tr className="border-b border-[#504A4A]/50">
                          <td className="px-4 py-1.5 text-[#EBFFEE] text-xs">John</td>
                          <td className="px-4 py-1.5 text-[#EBFFEE] text-xs">Doe</td>
                          <td className="px-4 py-1.5 text-[#EBFFEE] text-xs">john@example.com</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-1.5 text-[#EBFFEE] text-xs">Liam</td>
                          <td className="px-4 py-1.5 text-[#EBFFEE] text-xs">Chen</td>
                          <td className="px-4 py-1.5 text-[#EBFFEE] text-xs">liam@example.com</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="px-4 py-2 bg-[#363232]/50 border-t border-[#504A4A]">
                    <p className="text-xs text-[#A9A3A3]">
                      This is all you need. Add optional columns only if you have that data.
                    </p>
                  </div>
                </div>

                {/* Optional Columns */}
                <div>
                  <h3 className="font-semibold text-[#EBFFEE] mb-3">Optional Columns</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Phone Number</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Family ID</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Original Start Date</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Contract Start Date</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Contract End Date</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Subscription Price</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Subscription Name</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Subscription ID (UUID)</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Primary Location ID (UUID)</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Punch Card Total</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Punch Card Limit</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Trial Member (Yes/No)</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A9A3A3]">Notes</span>
                      <span className="text-[#A9A3A3]">Optional</span>
                    </div>
                  </div>
                </div>

                {/* Guidelines */}
                <div>
                  <h3 className="font-semibold text-[#EBFFEE] mb-3">Upload Guidelines</h3>
                  <ul className="text-sm text-[#A9A3A3] space-y-2">
                    <li className="flex items-start">
                      <span className="text-rollops-accent mr-2">•</span>
                      File must be in CSV format (.csv)
                    </li>
                    <li className="flex items-start">
                      <span className="text-rollops-accent mr-2">•</span>
                      Maximum file size: 10MB (supports large gyms and franchises)
                    </li>
                    <li className="flex items-start">
                      <span className="text-rollops-accent mr-2">•</span>
                      First row should contain column headers
                    </li>
                    <li className="flex items-start">
                      <span className="text-rollops-accent mr-2">•</span>
                      Email addresses must be unique per family (or unique if no family ID provided)
                    </li>
                    <li className="flex items-start">
                      <span className="text-rollops-accent mr-2">•</span>
                      Dates should be in MM-DD-YYYY format (e.g., 01-15-2025)
                    </li>
                  </ul>
                </div>

                {/* Support */}
                <div className="bg-[#363232] p-4 rounded-lg">
                  <h3 className="font-semibold text-[#EBFFEE] mb-2">Need Help?</h3>
                  <p className="text-[#A9A3A3] text-sm mb-3">
                    Having trouble with your upload? Our support team is here to help.
                  </p>
                  <a href="mailto:support@rollops.pro">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#504A4A] hover:text-[#EBFFEE] bg-transparent"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Contact Support
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Direct Invite Modal */}
      <DirectInviteModal
        isOpen={showDirectInviteModal}
        onClose={() => setShowDirectInviteModal(false)}
        onSuccess={() => {
          setShowDirectInviteModal(false);
          // Optionally navigate to account manager or show success message
        }}
      />
    </>
  )
}